//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useCallback } from 'react';
import { useTheme } from '@/theme/ThemeProvider';
import { router } from '@/routes';
import LoginRequiredException from '@/utils/LoginRequiredException';
import { ERoutesRoot } from '@/routes/RoutesEnum';

/**
 * Shared API-call wrapper for consistent error handling: a 401
 * (LoginRequiredException) logs out and redirects to the login page; any other
 * error is logged and re-thrown.  Used across the domain hooks and providers so
 * the 401 → logout/redirect policy lives in one place.
 *
 * Redirect goes through the router singleton (router.navigate) rather than the
 * useNavigate hook: this handler is used inside providers (e.g. AuthProvider)
 * that render above the RouterProvider, where useNavigate is unavailable.
 */
export function useApiErrorHandler() {
  const { handleLogout } = useTheme();

  return useCallback(async <T,>(
    functionLabel: string,
    apiCall: () => Promise<T>,
    logoutCallback: (() => void) | null = null
  ): Promise<T> => {
    try {
      return await apiCall();
    } catch (error) {
      if (error instanceof LoginRequiredException) {
        // Run any caller-supplied pre-logout cleanup first.
        if (logoutCallback !== null)
          logoutCallback();

        handleLogout();
        router.navigate(ERoutesRoot.Login);
        // Unreachable — navigate triggers unmount — but TypeScript needs a
        // terminal statement for the return type.
        throw error;
      } else {
        console.error(`API Error (${functionLabel})`, error);
        throw error;
      }
    }
  }, [handleLogout]);
}
